import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/format";
import { Save, RotateCcw } from "lucide-react";

const LVL = { E: "Επεξεργασία", V: "Προβολή", O: "Δικά μου", "-": "Κρυφό" };
const CLS = { E: "bg-[#E2ECE4] text-[#3F6B4C]", V: "bg-[#F3EADF] text-[#9A6A3A]", O: "bg-[#E8E4F2] text-[#5b4e82]", "-": "bg-[#F1EDE6] text-[#8A8577]" };

// Edits the ONE permissions grid (backend/permissions.py defaults + owner overrides in db.settings 'perm_grid').
export function PermissionGrid() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const { data } = useQuery({ queryKey: ["perm-grid"], queryFn: async () => (await api.get("/permissions/grid")).data });
  const [ov, setOv] = useState({});
  useEffect(() => {
    if (!data) return;
    const o = {};
    data.rows.forEach((r) => Object.entries(r.levels).forEach(([role, l]) => { if (l !== r.defaults[role]) (o[r.screen] ||= {})[role] = l; }));
    setOv(o);
  }, [data]);
  const save = useMutation({
    mutationFn: async () => (await api.put("/permissions/grid", { overrides: ov })).data,
    onSuccess: () => { toast.success("Το πλέγμα δικαιωμάτων αποθηκεύτηκε — ισχύει αμέσως για όλους"); qc.invalidateQueries({ queryKey: ["perm-grid"] }); qc.invalidateQueries({ queryKey: ["me"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });
  if (!data) return null;
  const roles = data.roles.filter((r) => r !== "owner");
  const lvlOf = (r, role) => ov[r.screen]?.[role] ?? r.defaults[role];
  const cycle = (r, role) => {
    if (!isOwner) return;
    const order = data.levels; const next = order[(order.indexOf(lvlOf(r, role)) + 1) % order.length];
    setOv((p) => { const row = { ...(p[r.screen] || {}) }; if (next === r.defaults[role]) delete row[role]; else row[role] = next; return { ...p, [r.screen]: row }; });
  };
  const changed = Object.values(ov).some((row) => Object.keys(row).length);
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="perm-grid">
      <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center gap-2">
        <div className="mr-auto">
          <h2 className="font-medium text-[#45423C]">Πλέγμα οθονών × ρόλων</h2>
          <p className="text-xs text-[#8A8577]">Μία πηγή αλήθειας για backend, μενού και σελίδες. Κλικ σε κελί για αλλαγή (Επεξεργασία → Προβολή → Δικά μου → Κρυφό). Ο Ιδιοκτήτης βλέπει πάντα τα πάντα.</p>
        </div>
        {isOwner && <button data-testid="perm-grid-reset" onClick={() => setOv({})} className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] px-3 py-2 text-sm"><RotateCcw className="h-4 w-4" /> Προεπιλογές</button>}
        {isOwner && <button data-testid="perm-grid-save" onClick={() => save.mutate()} disabled={save.isPending || !changed} className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"><Save className="h-4 w-4" /> Αποθήκευση</button>}
      </div>
      <div className="overflow-x-auto max-h-[70vh]">
        <table className="w-full text-xs">
          <thead className="sticky top-0">
            <tr className="bg-[#45423C] text-white/80 uppercase tracking-wider text-[10px]">
              <th className="px-3 py-2 text-left">Οθόνη</th>
              {roles.map((r) => <th key={r} className="px-2 py-2 text-center">{ROLE_LABEL[r] || r}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.screen} className="border-t border-[#E3DED4]">
                <td className="px-3 py-1.5 font-mono text-[#45423C] whitespace-nowrap">{r.screen}</td>
                {roles.map((role) => {
                  const l = lvlOf(r, role); const dirty = ov[r.screen]?.[role] !== undefined;
                  return (
                    <td key={role} className="px-1 py-1 text-center">
                      <button data-testid={`perm-${r.screen}-${role}`} onClick={() => cycle(r, role)} title={LVL[l]}
                        className={`w-full rounded-md px-1.5 py-1 font-semibold ${CLS[l]} ${dirty ? "ring-2 ring-[#C86235]" : ""}`}>{l}</button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
